import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toString } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL, IAuthorizedUserDto, IPage } from '@/dtos';
import { useAuthStore } from '@/modules/user/stores';

import {
  AuthorizedUser,
  AuthorizedUserStatusEnum,
  RelationshipTypeEnum,
} from '../authorizedUsers.types';

export const GET_AUTHORIZED_USERS_KEY = 'GET_AUTHORIZED_USERS_KEY';

const getAuthorizedUsers = (employeeId: string | number) =>
  api.get<IPage>(API_URL.EMPLOYEES_AUTHORIZED_USERS_BY_EMPLOYEE_ID(employeeId));

const useAuthorizedUsersQuery = () => {
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery(
    [GET_AUTHORIZED_USERS_KEY, user?.employee?.id],
    () => getAuthorizedUsers(user?.employee?.id ?? ''),
    { enabled: Boolean(user?.employee?.id) }
  );

  const formattedData = useMemo(
    () =>
      ((data?.data?.content ?? []) as IAuthorizedUserDto[])
        .filter((item) => item.status === AuthorizedUserStatusEnum.ACTIVE)
        .map((item: IAuthorizedUserDto): AuthorizedUser => ({
          id: toString(item.id),
          firstName: item.first_name ?? '',
          lastName: item.last_name ?? '',
          relationshipType: item.relationship_type ?? RelationshipTypeEnum.OTHER,
          birthOn: item.birth_on ? dayjs(item.birth_on).format(RAW_DATE_FORMAT) : '',
          status: item.status ?? AuthorizedUserStatusEnum.ACTIVE,
        })),
    [data]
  );

  return {
    authorizedUsers: formattedData,
    isLoading,
  };
};

export default useAuthorizedUsersQuery;
